import React, { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { formatEnglishDate } from "./utils/dateFormatting";
import "mapbox-gl/dist/mapbox-gl.css";
import LogoAppleTreeHD from "./LogoAppleTreeHD.svg";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { injected, walletConnect } from "@wagmi/connectors";
import { useNavigate } from "react-router-dom";
import { adminApi } from "./services/api";
import LegalFooter from "./components/legal/LegalFooter";
import LevelBadge from "./components/certification/LevelBadge";
import { resolveCompanyLogoUrl } from "./utils/companyLogo";

// Aggiungi CSS per rendere trasparente lo sfondo di Mapbox
const mapboxTransparentStyle = document.createElement('style');
mapboxTransparentStyle.textContent = `
  .mapboxgl-canvas-container.mapboxgl-interactive,
  .mapboxgl-canvas-container.mapboxgl-touch-zoom-rotate.mapboxgl-touch-drag-pan,
  .mapboxgl-canvas {
    background: transparent !important;
  }
  .mapboxgl-map {
    background: transparent !important;
  }
`;
if (!document.head.querySelector('style[data-mapbox-transparent]')) {
  mapboxTransparentStyle.setAttribute('data-mapbox-transparent', 'true');
  document.head.appendChild(mapboxTransparentStyle);
}

// CRA: variabili d'ambiente (trim to avoid trailing spaces/newlines)
const MAPBOX_TOKEN = (process.env.REACT_APP_MAPBOX_TOKEN || "").trim();
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").trim().replace(/\/$/, "");

const getLocationVisibilityLabel = (mapVisibility) => {
  switch (mapVisibility) {
    case "EXACT_ADDRESS":
      return "Exact address";
    case "CITY":
      return "City only";
    case "COUNTRY":
      return "Country only";
    case "HIDDEN":
      return "Hidden";
    default:
      return "Not specified";
  }
};

// Componente per la sezione del wallet
const WalletSection = () => {
  const { address, isConnected } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();

  const connectWallet = async () => {
    try {
      if (isConnected) return;

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isTrustWallet = window.ethereum?.isTrust === true;
      const isMetaMask = window.ethereum?.isMetaMask === true;
      const isBrave = window.ethereum?.isBraveWallet === true;

      if (isMobile) {
        if (window.ethereum) {
          if (isTrustWallet) {
            await connect({
              connector: walletConnect({ projectId: "f123d6b14f3a6461033ba05fd54cad5e", showQrModal: true }),
            });
            return;
          } else if (isMetaMask || isBrave) {
            await connect({ connector: injected() });
            return;
          }
        }
        await connect({
          connector: walletConnect({ projectId: "f123d6b14f3a6461033ba05fd54cad5e", showQrModal: true }),
        });
        return;
      }

      // Desktop browsers
      if (window.ethereum) {
        await connect({ connector: injected() });
      } else {
        await connect({
          connector: walletConnect({ projectId: "f123d6b14f3a6461033ba05fd54cad5e", showQrModal: true }),
        });
      }
    } catch (error) {
      console.error("Connection error:", error);
    }
  };

  return (
    <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 12 }}>
      {isConnected && address ? (
        <>
          <span style={{ 
            color: "#fff", 
            fontWeight: 600, 
            backgroundColor: "rgba(59, 130, 246, 0.25)",
            backdropFilter: "blur(8px)",
            padding: "8px 14px",
            borderRadius: "8px",
            border: "1px solid rgba(59, 130, 246, 0.4)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.4)",
            textShadow: "0 1px 3px rgba(0,0,0,0.8)"
          }}>
            🔗 {address.slice(0, 6)}...{address.slice(-4)}
          </span>
          <button
            onClick={() => disconnect()}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#c0392b";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#e74c3c";
              e.target.style.transform = "scale(1)";
            }}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              backgroundColor: "#e74c3c",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 500,
              transition: "all 0.2s ease",
            }}
          >
            Disconnect Wallet
          </button>
        </>
      ) : (
        <button
          onClick={connectWallet}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#15893d";
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#18A34A";
            e.target.style.transform = "scale(1)";
          }}
          style={{
            padding: "6px 12px",
            fontSize: 12,
            backgroundColor: "#18A34A",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
            fontWeight: 500,
            transition: "all 0.2s ease",
          }}
        >
          Connect Wallet
        </button>
      )}
    </div>
  );
};

function ESGDashboard() {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // Indirizzo wallet admin
  const ADMIN_WALLET_ADDRESS = "0x8e46AF311F328FDa5B5F8c648BB91B6e2022D4a3".toLowerCase();

  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [errorCompanies, setErrorCompanies] = useState("");
  const [mapInitError, setMapInitError] = useState("");
  
  // Level filters
  const [selectedLevels, setSelectedLevels] = useState(["NONE", "VERIFIED", "TRUSTED", "IMPACT", "REGEN"]);
  const [companySearch, setCompanySearch] = useState("");

  const [adminMode, setAdminMode] = useState(false);
  const [adminKey, setAdminKey] = useState("");
  const [adminKeyOk, setAdminKeyOk] = useState(false);
  
  const [esgRequestsView, setEsgRequestsView] = useState(false);
  const [esgRequests, setEsgRequests] = useState([]);
  const [loadingESG, setLoadingESG] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Verifica se l'utente è l'admin
  const isAdmin = isConnected && address?.toLowerCase() === ADMIN_WALLET_ADDRESS;

  // Funzione per navigare al pannello admin
  const goToAdminPanel = () => {
    navigate('/admin/esg');
  };

  const [formData, setFormData] = useState({
    name: "",
    country: "",
    wallet: "",
    score: "A",
    latitude: "",
    longitude: "",
  });

  const normalizedSearch = useMemo(() => companySearch.trim().toLowerCase(), [companySearch]);

  const filteredCompanies = useMemo(() => {
    return companies
      .filter((c) => selectedLevels.includes(c.level || c.certificationLevel || "NONE"))
      .filter((c) => {
        if (!normalizedSearch) return true;

        const primitiveFields = Object.entries(c || {})
          .filter(([, value]) => {
            const t = typeof value;
            return t === "string" || t === "number" || t === "boolean";
          })
          .map(([, value]) => String(value));

        const searchable = [
          c?.name,
          c?.companyName,
          c?.country,
          c?.city,
          c?.region,
          c?.address,
          c?.sector,
          c?.businessSector,
          c?.walletAddress,
          c?.wallet,
          c?.companyWallet,
          c?.reportCID,
          c?.ipfsCID,
          c?.cid,
          c?.ipfsJsonCid,
          c?.ipfsPdfCid,
          c?.jsonCID,
          c?.pdfCID,
          c?.txHash,
          c?.transactionHash,
          c?.certificationTxHash,
          c?.level,
          c?.certificationLevel,
          ...(c?.badges || []),
          ...primitiveFields,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(" ")
          .toLowerCase();

        return searchable.includes(normalizedSearch);
      });
  }, [companies, selectedLevels, normalizedSearch]);

  const totalCountriesCovered = useMemo(() => {
    const set = new Set();
    companies.forEach((c) => {
      const country = (c?.country || "").trim();
      if (country) set.add(country);
    });
    return set.size;
  }, [companies]);

  const countriesCovered = useMemo(() => {
    const set = new Set();
    filteredCompanies.forEach((c) => {
      const country = (c?.country || "").trim();
      if (country) set.add(country);
    });
    return set.size;
  }, [filteredCompanies]);

  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [removingCompany, setRemovingCompany] = useState(false);
  const [removeWallet, setRemoveWallet] = useState("");
  const [removeMessage, setRemoveMessage] = useState("");

  // === 1) Fetch certified companies from public endpoint ===
  async function fetchCompanies() {
    if (!API_BASE_URL) {
      setErrorCompanies("API base URL mancante - controllare REACT_APP_API_BASE_URL nello .env");
      return;
    }

    setLoadingCompanies(true);
    setErrorCompanies("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/public/certified-companies`);
      if (!res.ok) throw new Error("Server response error");
      const data = await res.json();
      console.log("🔍 Certified companies data:", data);
      if (data && data.length > 0) {
        console.log("🎯 First company sample:", data[0]);
        console.log("📊 Badge fields check:", {
          "badges": data[0].badges,
          "badges.length": data[0].badges?.length,
          "badgesCount": data[0].badgesCount,
          "badgeCount": data[0].badgeCount,
          "certification.badges": data[0].certification?.badges,
          "certification.badges.length": data[0].certification?.badges?.length,
          "publicCertification.badges": data[0].publicCertification?.badges,
          "publicCertification.badges.length": data[0].publicCertification?.badges?.length,
        });
      }
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setErrorCompanies("Unable to load certified companies");
    } finally {
      setLoadingCompanies(false);
    }
  }

  useEffect(() => {
    fetchCompanies();
  }, []);

  // === 2) Inizializza la mappa Mapbox (globo sempre visibile) ===
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return; // già inizializzata

    if (!MAPBOX_TOKEN) {
      const errorMsg = "MAPBOX_TOKEN mancante - controllare .env";
      console.error(errorMsg);
      setMapInitError(errorMsg);
      return;
    }

    try {
      mapboxgl.accessToken = MAPBOX_TOKEN;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
          style: "mapbox://styles/mapbox/satellite-streets-v12",
        center: [10, 30],
        zoom: 1.3,
        projection: "globe",
      });

      mapRef.current = map;

      map.on("style.load", () => {
        // Rimuovi effetto spazio/stellato e rendi trasparente lo sfondo
        map.setFog({
          "range": [0.5, 10],
          "horizon-blend": 0.0,
          "color": "rgba(0,0,0,0)",
          "high-color": "rgba(0,0,0,0)",
          "space-color": "rgba(0,0,0,0)",
          "star-intensity": 0.0,
        });

        // background layer trasparente
        try {
          map.setPaintProperty("background", "background-color", "rgba(0,0,0,0)");
          map.setPaintProperty("background", "background-opacity", 0);
        } catch (e) {
          console.warn("Background paint not set", e);
        }

        // Assicurati che il canvas sia trasparente
        const canvas = map.getCanvas();
        if (canvas) {
          canvas.style.backgroundColor = "transparent";
          if (canvas.parentNode && canvas.parentNode.style) {
            canvas.parentNode.style.backgroundColor = "transparent";
          }
        }

        // Acqua realistica multi-layer: profondità scura + shimmer costiero chiaro
        try {
          const firstSymbolLayerId = map
            .getStyle()
            ?.layers?.find((layer) => layer.type === "symbol")?.id;

          // Layer 1 — base profondità: più scuro in alto mare, si schiarisce avvicinandosi (zoom in)
          if (!map.getLayer("water-depth-base")) {
            map.addLayer(
              {
                id: "water-depth-base",
                type: "fill",
                source: "composite",
                "source-layer": "water",
                paint: {
                  "fill-color": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0,  "#2F7EC0",   // oceano profondo - blu scuro
                    4,  "#3490CC",
                    7,  "#3FA7E0",   // media profondità - azzurro medio
                    10, "#52BCEE",
                    13, "#5EC6FF",   // acque basse - azzurro chiaro
                  ],
                  "fill-opacity": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0,  0.80,
                    6,  0.85,
                    12, 0.88,
                  ],
                  "fill-outline-color": "#A8DEFF",
                },
              },
              firstSymbolLayerId
            );
          }

          // Layer 2 — shimmer costiero: invisibile in oceano aperto, diventa visibile con lo zoom
          // simula le acque basse e le coste più chiare
          if (!map.getLayer("water-coastal-shimmer")) {
            map.addLayer(
              {
                id: "water-coastal-shimmer",
                type: "fill",
                source: "composite",
                "source-layer": "water",
                paint: {
                  "fill-color": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    5,  "#80D8FF",
                    9,  "#9EE9FF",
                    13, "#B8F0FF",
                  ],
                  "fill-opacity": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    4,  0.0,    // invisibile in modalità globo
                    7,  0.10,   // quasi impercettibile
                    10, 0.20,   // leggermente più chiaro vicino alle coste
                    13, 0.28,   // shimmer costiero evidente
                  ],
                  "fill-outline-color": "#C8F2FF",
                },
              },
              firstSymbolLayerId
            );
          }
        } catch (e) {
          console.warn("Water overlay not applied", e);
        }

        if (!map.getSource("certified-companies")) {
          map.addSource("certified-companies", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [],
            },
          });
        }

        // Circle layer (green dots)
        if (!map.getLayer("companies-circles")) {
          map.addLayer({
            id: "companies-circles",
            type: "circle",
            source: "certified-companies",
            paint: {
              "circle-radius": 8,
              "circle-color": "#13FF00",
              "circle-opacity": 0.85,
              "circle-stroke-width": 0,
            },
          });
        }

        if (!map.getLayer("companies-layer")) {
          map.addLayer({
            id: "companies-layer",
            type: "symbol",
            source: "certified-companies",
            layout: {
              "icon-image": "marker-15",
              "icon-size": 1.2,
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "text-field": ["get", "name"],
              "text-size": 0,
              "text-offset": [0, 0.8],
              "text-anchor": "top",
            },
          });
        }

        map.on("click", "companies-circles", (e) => {
          if (!e.features || e.features.length === 0) return;
          const feature = e.features[0];
          const props = feature.properties || {};

          const level = props.level || "NONE";
          const badgeCount = props.badgeCount || 0;
          const certificationId = props.certificationId || props.publicCertificationId || props.certId || "";

          const levelConfig = {
            NONE: { label: "Not Certified", color: "#9CA3AF" },
            VERIFIED: { label: "Verified", color: "#F97316" },
            TRUSTED: { label: "Trusted", color: "#06B6D4" },
            IMPACT: { label: "Impact", color: "#FFEB3B" },
            REGEN: { label: "Regenerative", color: "#84CC16" },
          };
          const levelInfo = levelConfig[level] || levelConfig.NONE;

          const popupHTML = `
            <div style="min-width: 260px; font-family: system-ui, -apple-system, sans-serif; background: #ffffff; border-radius: 10px; box-shadow: 0 10px 25px rgba(0,0,0,0.15);">
              <div style="font-size: 16px; font-weight: 700; color: #0f172a; margin-bottom: 10px; padding: 12px 12px 0 12px;">
                ${props.name || "Unknown Company"}
              </div>

              <div style="margin: 12px; padding: 12px; background: ${levelInfo.color}; border-radius: 8px; text-align: center;">
                <div style="font-size: 14px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px;">
                  ${levelInfo.label}
                </div>
                <div style="font-size: 12px; font-weight: 500; color: #fff; margin-top: 4px; opacity: 0.95;">
                  ${badgeCount} badges earned
                </div>
              </div>

              <div style="font-size: 11px; color: #374151; margin: 0 12px 12px 12px; padding: 10px; background: #f3f4f6; border-radius: 6px; border-left: 4px solid #3b82f6;">
                <div style="margin-bottom: 4px;"><strong style="color: #1f2937;">Issued:</strong> ${formatEnglishDate(props.issuedAt)}</div>
                <div><strong style="color: #1f2937;">Expires:</strong> ${formatEnglishDate(props.expiresAt)}</div>
              </div>

              <div style="font-size: 11px; color: #374151; margin: 0 12px 12px 12px; padding: 10px; background: #eff6ff; border-radius: 6px; border-left: 4px solid #0ea5e9;">
                <strong style="color: #1f2937;">Visibility:</strong> ${getLocationVisibilityLabel(props.mapVisibility)}
              </div>

              <div style="margin: 0 12px 12px 12px; display: flex; flex-direction: column; gap: 8px;">
                ${certificationId ? `
                  <a
                    href="/verify/${encodeURIComponent(certificationId)}"
                    style="display: block; text-align: center; text-decoration: none; padding: 10px 14px; background: linear-gradient(135deg, #0ea5e9, #0369a1); color: white; border-radius: 6px; font-size: 13px; font-weight: 600; transition: all 0.2s; box-shadow: 0 2px 6px rgba(0,0,0,0.1);"
                  >
                    🏅 View Public Certification
                  </a>
                ` : ""}

                ${props.pdfCID ? `
                  <a
                    href="https://cyan-elaborate-fowl-975.mypinata.cloud/ipfs/${encodeURIComponent(props.pdfCID)}"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="display: block; text-align: center; text-decoration: none; padding: 10px 14px; background: linear-gradient(135deg, #3b82f6, #1d4ed8); color: white; border-radius: 6px; font-size: 13px; font-weight: 600; transition: all 0.2s; box-shadow: 0 2px 6px rgba(0,0,0,0.1);"
                  >
                    📄 View PDF Report
                  </a>
                ` : ""}

                ${props.txHash ? `
                  <a
                    href="https://etherscan.io/tx/${props.txHash}"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="display: block; text-align: center; text-decoration: none; padding: 10px 14px; background: linear-gradient(135deg, #8b5cf6, #6d28d9); color: white; border-radius: 6px; font-size: 13px; font-weight: 600; transition: all 0.2s; box-shadow: 0 2px 6px rgba(0,0,0,0.1);"
                  >
                    ⛓️ Verify On-Chain
                  </a>
                ` : ""}
              </div>
            </div>
          `;

          new mapboxgl.Popup({ maxWidth: "320px" })
            .setLngLat(feature.geometry.coordinates)
            .setHTML(popupHTML)
            .addTo(map);
        });

      let hoverPopup = null;

      map.on("mouseenter", "companies-circles", (e) => {
        map.getCanvas().style.cursor = "pointer";
        if (e.features.length > 0) {
          const feature = e.features[0];
          if (hoverPopup) hoverPopup.remove();
          hoverPopup = new mapboxgl.Popup({
            closeButton: false,
            offset: [0, -25],
            anchor: "bottom",
            className: "mapbox-hover-popup",
          })
            .setLngLat(e.lngLat)
            .setHTML(`<div style="background: #1e293b; color: #f1f5f9; padding: 8px 12px; border-radius: 6px; font-size: 12px; font-weight: 500; max-width: 200px; text-align: center; border: 1px solid rgba(148, 163, 184, 0.3);">${feature.properties.name}</div>`)
            .addTo(map);
        }
      });
      map.on("mouseleave", "companies-circles", () => {
        map.getCanvas().style.cursor = "";
        if (hoverPopup) hoverPopup.remove();
      });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
    } catch (error) {
      const errorMsg = "Map initialization error: " + error.message;
      console.error(errorMsg, error);
      setMapInitError(errorMsg);
    }
  }, []);

  // === 3) Aggiorna i punti sulla mappa quando cambia "companies" ===
  useEffect(() => {
    const map = mapRef.current;

    // Se la mappa non è pronta, esco
    if (!map || typeof map.getSource !== "function") {
      return;
    }

    const source = map.getSource("certified-companies");

    // Se la sorgente non c'è ancora (style non caricato), esco
    if (!source) {
      return;
    }

    // Filter: only render if lat/lng are present (not null/undefined)
    const features = filteredCompanies
      .filter((c) => {
        // Exclude if coordinates are null/undefined/empty
        if (c.latitude == null || c.longitude == null) return false;
        if (c.latitude === "" || c.longitude === "") return false;
        // Exclude if mapVisibility is HIDDEN
        if (c.mapVisibility === "HIDDEN") return false;
        return true;
      })
      .map((c) => ({
        type: "Feature",
        properties: {
          id: c.id || c._id,
          name: c.name || c.companyName,
          country: c.country,
          sector: c.sector || c.businessSector,
          level: c.level || c.certificationLevel || c.certification?.level || "NONE",
          badgeCount: c.badges?.length || c.certification?.badges?.length || c.publicCertification?.badges?.length || c.badgesCount || c.badgeCount || 0,
          issuedAt: c.issuedAt || c.certifiedAt || c.createdAt,
          expiresAt: c.expiresAt || c.expirationDate,
          mapVisibility: c.mapVisibility,
          reportCID: c.reportCID || c.ipfsCID || c.cid,
          pdfCID: c.pdfCID || c.ipfsPdfCid,
          txHash: c.txHash || c.transactionHash || c.certificationTxHash,
          walletAddress: c.walletAddress || c.wallet || c.companyWallet,
          certificationId:
            c.certificationId ||
            c.certification_id ||
            c.publicCertificationId ||
            c.public_certification_id ||
            c.certId ||
            c.cert_id,
          publicCertificationId: c.publicCertificationId || c.public_certification_id,
          certId: c.certId || c.cert_id,
        },
        geometry: {
          type: "Point",
          coordinates: [c.longitude, c.latitude],
        },
      }));

    try {
      source.setData({
        type: "FeatureCollection",
        features,
      });
    } catch (err) {
      console.error("Error updating Mapbox source:", err);
    }
  }, [filteredCompanies]);

  // === 4) Gestione form admin ===
  async function handleAddCompany(e) {
    e.preventDefault();
    setSaveMessage("");

    if (!adminKeyOk) {
      setSaveMessage("Please enter a valid admin key.");
      return;
    }

    if (
      !formData.name ||
      !formData.country ||
      !formData.wallet ||
      !formData.latitude ||
      !formData.longitude
    ) {
      setSaveMessage("Compila tutti i campi obbligatori.");
      return;
    }

    const body = {
      name: formData.name,
      country: formData.country,
      walletAddress: formData.wallet,
      score: formData.score,
      latitude: Number(formData.latitude),
      longitude: Number(formData.longitude),
    };

    setSavingCompany(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/companies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": adminKey,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Error saving company");
      }

      const newCompany = await res.json();
      setSaveMessage("Company added successfully ✅");
      setFormData({
        name: "",
        country: "",
        wallet: "",
        score: "A",
        latitude: "",
        longitude: "",
      });

      setCompanies((prev) => [...prev, newCompany]);
    } catch (err) {
      console.error(err);
      setSaveMessage(err.message || "Error saving company");
    } finally {
      setSavingCompany(false);
    }
  }

  function normalizeWallet(value) {
    return (value || "").trim().toLowerCase();
  }

  async function handleRemoveCompany(e) {
    e.preventDefault();
    setRemoveMessage("");

    if (!adminKeyOk) {
      setRemoveMessage("Please enter a valid admin key.");
      return;
    }

    if (!API_BASE_URL) {
      setRemoveMessage("API base URL mancante - controllare REACT_APP_API_BASE_URL nello .env");
      return;
    }

    const targetWallet = normalizeWallet(removeWallet);
    if (!targetWallet) {
      setRemoveMessage("Enter the company's wallet to remove.");
      return;
    }

    const matchedCompany = companies.find((c) => {
      const w = normalizeWallet(c.walletAddress || c.wallet || c.address);
      return w && w === targetWallet;
    });

    const targetId = matchedCompany?.id || matchedCompany?._id;

    setRemovingCompany(true);
    try {
      let res;
      if (targetId) {
        res = await fetch(`${API_BASE_URL}/api/companies/${targetId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-admin-key": adminKey,
          },
        });
      } else {
        res = await fetch(
          `${API_BASE_URL}/api/companies/by-wallet/${encodeURIComponent(targetWallet)}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              "x-admin-key": adminKey,
            },
          }
        );
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Error removing company");
      }

      setCompanies((prev) =>
        prev.filter((c) => {
          const w = normalizeWallet(c.walletAddress || c.wallet || c.address);
          const cid = c.id || c._id;
          if (targetId) return cid !== targetId;
          return w !== targetWallet;
        })
      );
      setRemoveWallet("");
      setRemoveMessage("Company removed (if present) ✅");
    } catch (err) {
      console.error(err);
      setRemoveMessage(err.message || "Error removing company");
    } finally {
      setRemovingCompany(false);
    }
  }

  function handleAdminKeyCheck() {
    if (!adminKey) return;
    setAdminKeyOk(true); // il controllo vero lo fa il backend
  }

  function flyToCompany(company) {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({
      center: [company.longitude, company.latitude],
      zoom: 4,
      essential: true,
    });
  }

  // === ESG Admin Functions ===
  async function fetchESGRequests() {
    setLoadingESG(true);
    try {
      const requests = await adminApi.getRequests({}, adminKey, address);
      setEsgRequests(requests);
    } catch (err) {
      console.error("Error fetching ESG requests:", err);
      if (err.message && err.message.includes('Endpoint ESG non trovato')) {
        alert("⚠️ Backend Non Pronto\n\nGli endpoint ESG non sono ancora implementati sul backend.\n\nVerifica il file BACKEND_ESG_ENDPOINTS_REQUIRED.md per i dettagli.");
      } else {
        alert("Error loading ESG requests: " + err.message);
      }
      setEsgRequests([]);
    } finally {
      setLoadingESG(false);
    }
  }

  async function handleApproveESG(requestId) {
    if (!window.confirm("Approve this ESG request?")) return;
    try {
      await adminApi.approveRequest(requestId, "", adminKey, address);
      alert("Request approved successfully");
      fetchESGRequests();
      setSelectedRequest(null);
    } catch (err) {
      console.error("Error approving request:", err);
      alert("Error approving request: " + err.message);
    }
  }

  async function handleRejectESG(requestId) {
    const reason = window.prompt("Reason for rejection (optional):");
    if (reason === null) return;
    try {
      await adminApi.rejectRequest(requestId, reason || "Rejected by admin", adminKey, address);
      alert("Request rejected");
      fetchESGRequests();
      setSelectedRequest(null);
    } catch (err) {
      console.error("Error rejecting request:", err);
      alert("Error rejecting request: " + err.message);
    }
  }

  async function handleDownloadZip(requestId) {
    try {
      const downloadUrl = await adminApi.getZipDownloadUrl(requestId, adminKey);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `esg_request_${requestId}_attachments.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading zip:", err);
      alert("Error downloading zip: " + err.message);
    }
  }

  useEffect(() => {
    if (esgRequestsView && adminKeyOk) {
      fetchESGRequests();
    }
  }, [esgRequestsView, adminKeyOk]);

  // --- UI ---
  return (
    <div
      className="esg-root"
      style={{
        minHeight: "100vh",
        background: 'url("/assets/sfondo-dashboard.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        color: "#f5f5f5",
        textShadow: "0 2px 6px rgba(0,0,0,0.7)",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* HEADER */}
      <header
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={LogoAppleTreeHD}
            alt="AppleTree Logo"
            onClick={() => navigate("/")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.transition = "transform 0.15s ease";
              e.currentTarget.style.cursor = "pointer";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>AppleTree</div>
            <div
              style={{
                fontSize: 11,
                opacity: 1,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: "#a5f3fc",
                textShadow: "0 2px 8px rgba(0,0,0,0.9), 0 0 12px rgba(165,243,252,0.4)",
                fontWeight: 600,
              }}
            >
              ESG Certification Dashboard
            </div>
          </div>
        </div>

        <WalletSection />
      </header>

      {/* BODY */}
      <main
        className="dashboard-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 420px) minmax(0, 1fr)",
          gap: 24,
          padding: "20px 24px 24px",
        }}
      >
        {/* COLONNA SINISTRA */}
        <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Card verifica */}
          <div
            style={{
              padding: 16,
              borderRadius: 16,
              background:
                "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))",
              border: "1px solid rgba(124,255,178,0.25)",
              boxShadow: "0 18px 35px rgba(0,0,0,0.55)",
              backdropFilter: "blur(6px)",
              textShadow: "0 2px 6px rgba(0,0,0,0.75)",
            }}
          >
            <h2 style={{ fontSize: 18, margin: 0, marginBottom: 6, color: "#f8fafc" }}>
              Verify your company
            </h2>
            <p
              style={{
                margin: 0,
                marginBottom: 12,
                fontSize: 13,
                color: "#E6FFE8",
              }}
            >
              Start your on-chain ESG verification and earn the AppleTree badge for your company.
            </p>

            <button
              style={{
                width: "100%",
                padding: "10px 16px",
                borderRadius: 999,
                border: "none",
                background:
                  "linear-gradient(135deg, #7CFFB2, #22C55E, #15803D)",
                color: "#020617",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                boxShadow: "0 8px 18px rgba(34,197,94,0.4)",
                textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                navigate("/company-verification");
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.05)";
                e.target.style.boxShadow = "0 12px 24px rgba(34,197,94,0.6)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 8px 18px rgba(34,197,94,0.4)";
              }}
            >
              Start company verification
            </button>

            <div style={{ marginTop: 10, fontSize: 11, opacity: 0.7 }}>
              ⏱ Average verification time: 24–72h
            </div>

            <div
              style={{
                marginTop: 14,
                fontSize: 12,
                lineHeight: 1.5,
                background: "rgba(59,130,246,0.08)",
                border: "1px solid rgba(59,130,246,0.25)",
                borderRadius: 10,
                padding: "10px 12px",
                color: "#dbeafe",
              }}
            >
              Learn about the{' '}
              <a
                href="https://appletreetoken.com/treasury-distribution-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#7CFFB2", textDecoration: "underline" }}
                onMouseEnter={(e) => (e.target.style.color = "#a5f3fc")}
                onMouseLeave={(e) => (e.target.style.color = "#7CFFB2")}
              >
                treasury distribution policy
              </a>
              .
            </div>
          </div>

          {/* Admin Panel - Visibile solo se admin */}
          {isConnected && isAdmin && (
            <div
              style={{
                padding: 16,
                borderRadius: 16,
                background: "linear-gradient(145deg, rgba(147,51,234,0.16), rgba(5,8,22,0.92))",
                border: "1px solid rgba(168,85,247,0.4)",
                boxShadow: "0 18px 35px rgba(0,0,0,0.55)",
                backdropFilter: "blur(6px)",
                fontSize: 12,
              }}
            >
              <h2 style={{ fontSize: 18, margin: 0, marginBottom: 6, color: "#f8fafc", textShadow: "0 2px 6px rgba(0,0,0,0.75)" }}>
                🔐 Admin Panel
              </h2>
              <p style={{ margin: 0, marginBottom: 12, fontSize: 13, color: "#e9d5ff" }}>
                Gestisci le richieste ESG, approva o rifiuta verifiche aziendali.
              </p>
              
              <button
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  borderRadius: 999,
                  border: "none",
                  background: "linear-gradient(135deg, #a855f7, #7c3aed, #6d28d9)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  boxShadow: "0 8px 18px rgba(168,85,247,0.4)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                  transition: "all 0.2s ease",
                  marginBottom: 12,
                }}
                onClick={() => navigate("/admin/esg")}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.05)";
                  e.target.style.boxShadow = "0 12px 24px rgba(168,85,247,0.6)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 8px 18px rgba(168,85,247,0.4)";
                }}
              >
                ⚡ Apri ESG Requests Panel
              </button>
              
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                  paddingTop: 12,
                  borderTop: "1px solid rgba(148,163,184,0.2)",
                }}
              >
                <span style={{ opacity: 0.9, color: "#e9d5ff" }}>Company Admin Mode</span>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={adminMode}
                    onChange={(e) => setAdminMode(e.target.checked)}
                  />
                  <span style={{ opacity: 0.8 }}>Show panel</span>
                </label>
              </div>

              {adminMode && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: 11, opacity: 0.8 }}>
                    Admin key (additional security)
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="password"
                      value={adminKey}
                      onChange={(e) => {
                        setAdminKey(e.target.value);
                        setAdminKeyOk(false);
                      }}
                      placeholder="Enter admin key…"
                      style={{
                        flex: 1,
                        padding: "6px 10px",
                        borderRadius: 999,
                        border: "1px solid rgba(148,163,184,0.7)",
                        background: "rgba(15,23,42,0.9)",
                        color: "#f9fafb",
                        fontSize: 12,
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAdminKeyCheck}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: "none",
                        background: adminKeyOk ? "#16a34a" : "#1d4ed8",
                        color: "#f9fafb",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      OK
                    </button>
                  </div>
                  {adminKeyOk && (
                    <>
                      <div style={{ fontSize: 11, color: "#4ade80" }}>
                        ✅ Admin key set (server will verify it).
                      </div>
                      <button
                        type="button"
                        onClick={() => setEsgRequestsView(!esgRequestsView)}
                        style={{
                          marginTop: 8,
                          padding: "8px 14px",
                          borderRadius: 999,
                          border: "none",
                          background: esgRequestsView ? "#059669" : "#0ea5e9",
                          color: "#f9fafb",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {esgRequestsView ? "Hide ESG Requests" : "View ESG Requests"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Admin panel card to add companies - Visible only if admin */}
          {isAdmin && adminMode && (
            <div
              style={{
                padding: 16,
                borderRadius: 16,
                background: "rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.4)",
                boxShadow: "0 12px 25px rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <h3 style={{ fontSize: 15, margin: 0, marginBottom: 4 }}>
                Add Certified Company
              </h3>
              <form onSubmit={handleAddCompany} style={{ display: "grid", gap: 8 }}>
                <input
                  type="text"
                  placeholder="Company name*"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, name: e.target.value }))
                  }
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Country (e.g. Italy)*"
                  value={formData.country}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, country: e.target.value }))
                  }
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Company wallet address*"
                  value={formData.wallet}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, wallet: e.target.value }))
                  }
                  style={inputStyle}
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="ESG Score (0-100)*"
                  value={formData.score}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Assicura che il valore sia tra 0 e 100
                    if (val === "" || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                      setFormData((f) => ({ ...f, score: val }));
                    }
                  }}
                  style={inputStyle}
                />
                <input
                  type="number"
                  step="any"
                  placeholder="Latitude* (e.g. 41.9028)"
                  value={formData.latitude}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, latitude: e.target.value }))
                  }
                  style={inputStyle}
                />
                <input
                  type="number"
                  step="any"
                  placeholder="Longitude* (e.g. 12.4964)"
                  value={formData.longitude}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, longitude: e.target.value }))
                  }
                  style={inputStyle}
                />

                <button
                  type="submit"
                  disabled={savingCompany}
                  style={{
                    marginTop: 4,
                    padding: "8px 14px",
                    borderRadius: 999,
                    border: "none",
                    background: savingCompany ? "#6b7280" : "#22c55e",
                    color: "#020617",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: savingCompany ? "default" : "pointer",
                  }}
                >
                  {savingCompany ? "Saving..." : "Save Company"}
                </button>
              </form>
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 8,
                  borderTop: "1px solid rgba(148,163,184,0.2)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <h4 style={{ margin: 0, fontSize: 13 }}>Remove Certified Company</h4>
                <form onSubmit={handleRemoveCompany} style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Company wallet address"
                    value={removeWallet}
                    onChange={(e) => setRemoveWallet(e.target.value)}
                    style={{ ...inputStyle, borderRadius: 12 }}
                  />
                  <button
                    type="submit"
                    disabled={removingCompany}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 12,
                      border: "none",
                      background: removingCompany ? "#6b7280" : "#ef4444",
                      color: "#f8fafc",
                      fontWeight: 700,
                      cursor: removingCompany ? "default" : "pointer",
                    }}
                  >
                    {removingCompany ? "Removing..." : "Remove"}
                  </button>
                </form>
                <div style={{ fontSize: 11, opacity: 0.75 }}>
                  Match by wallet; backend receives DELETE with admin key.
                </div>
              </div>
              {saveMessage && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    color: saveMessage.includes("successfully")
                      ? "#4ade80"
                      : "#f97373",
                  }}
                >
                  {saveMessage}
                </div>
              )}
              {removeMessage && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: removeMessage.includes("✅") ? "#4ade80" : "#f97373",
                  }}
                >
                  {removeMessage}
                </div>
              )}
            </div>
          )}

          {/* ESG Requests Admin Panel */}
          {isAdmin && adminMode && esgRequestsView && (
            <div
              style={{
                padding: 16,
                borderRadius: 16,
                background: "rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.4)",
                boxShadow: "0 12px 25px rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <h3 style={{ fontSize: 15, margin: 0, marginBottom: 4 }}>
                ESG Verification Requests
              </h3>

              {loadingESG && (
                <div style={{ fontSize: 12, opacity: 0.8 }}>Loading requests...</div>
              )}

              {!loadingESG && esgRequests.length === 0 && (
                <div style={{ fontSize: 12, opacity: 0.7 }}>No requests yet.</div>
              )}

              {!loadingESG && esgRequests.length > 0 && (
                <div
                  style={{
                    maxHeight: 400,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {esgRequests.map((req) => (
                    <div
                      key={req.id}
                      style={{
                        padding: 12,
                        borderRadius: 8,
                        background: "rgba(30,41,59,0.8)",
                        border: "1px solid rgba(148,163,184,0.3)",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                        {req.companyName || "N/A"}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 6 }}>
                        Status: <span style={{ color: req.status === "pending" ? "#fbbf24" : req.status === "approved" ? "#4ade80" : "#f87171" }}>{req.status}</span>
                        {" | "}
                        Submitted: {formatEnglishDate(req.createdAt)}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button
                          onClick={() => setSelectedRequest(req)}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: "#0ea5e9",
                            color: "#f9fafb",
                            fontSize: 11,
                            cursor: "pointer",
                          }}
                        >
                          View Details
                        </button>
                        {req.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleApproveESG(req.id)}
                              style={{
                                padding: "4px 10px",
                                borderRadius: 6,
                                border: "none",
                                background: "#16a34a",
                                color: "#f9fafb",
                                fontSize: 11,
                                cursor: "pointer",
                              }}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectESG(req.id)}
                              style={{
                                padding: "4px 10px",
                                borderRadius: 6,
                                border: "none",
                                background: "#dc2626",
                                color: "#f9fafb",
                                fontSize: 11,
                                cursor: "pointer",
                              }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDownloadZip(req.id)}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: "#7c3aed",
                            color: "#f9fafb",
                            fontSize: 11,
                            cursor: "pointer",
                          }}
                        >
                          Download ZIP
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Certified companies list card */}
          <div
            style={{
              padding: 16,
              borderRadius: 16,
              background:
                "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))",
              border: "1px solid rgba(124,255,178,0.4)",
              boxShadow: "0 18px 35px rgba(0,0,0,0.55)",
              backdropFilter: "blur(6px)",
              textShadow: "0 2px 6px rgba(0,0,0,0.75)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <h3 style={{ fontSize: 15, margin: 0, color: "#f8fafc" }}>
              Certified Companies ({filteredCompanies.length})
            </h3>
            
            {/* Level Filters */}
            <div style={{ 
              display: "flex", 
              flexWrap: "wrap", 
              gap: 6,
              padding: "8px 0",
              borderBottom: "1px solid rgba(124,255,178,0.2)"
            }}>
              {["REGEN", "IMPACT", "TRUSTED", "VERIFIED", "NONE"].map((level) => {
                const levelConfig = {
                  REGEN: { label: "Regenerative", color: "#84CC16" },
                  IMPACT: { label: "Impact", color: "#FFEB3B" },
                  TRUSTED: { label: "Trusted", color: "#06B6D4" },
                  VERIFIED: { label: "Verified", color: "#F97316" },
                  NONE: { label: "Not Certified", color: "#9CA3AF" }
                };
                const config = levelConfig[level];
                const isSelected = selectedLevels.includes(level);
                
                return (
                  <button
                    key={level}
                    onClick={() => {
                      setSelectedLevels(prev => 
                        isSelected 
                          ? prev.filter(l => l !== level)
                          : [...prev, level]
                      );
                    }}
                    style={{
                      padding: "4px 8px",
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: 6,
                      border: `1.5px solid ${isSelected ? config.color : "rgba(148,163,184,0.4)"}`,
                      background: isSelected ? `${config.color}33` : "rgba(15,23,42,0.6)",
                      color: isSelected ? config.color : "#94a3b8",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.borderColor = config.color;
                        e.target.style.color = config.color;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.borderColor = "rgba(148,163,184,0.4)";
                        e.target.style.color = "#94a3b8";
                      }
                    }}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>

            <input
              type="text"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              placeholder="Search by name, wallet, sector, country, CID, tx hash..."
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid rgba(124,255,178,0.3)",
                background: "rgba(15,23,42,0.75)",
                color: "#f8fafc",
                fontSize: 12,
                outline: "none",
              }}
            />

            {loadingCompanies && (
              <div style={{ fontSize: 12, opacity: 0.8 }}>Loading…</div>
            )}
            {errorCompanies && (
              <div style={{ fontSize: 12, color: "#f97373" }}>
                {errorCompanies}
              </div>
            )}

            <div
              style={{
                maxHeight: 220,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingRight: 4,
              }}
            >
              {filteredCompanies.map((c) => {
                const logoUrl = resolveCompanyLogoUrl(c);
                const companyName = c.name || c.companyName || "Unknown";

                return (
                  <button
                    key={c.id || c._id || c.walletAddress || c.wallet || companyName}
                    onClick={() => flyToCompany(c)}
                    style={{
                      textAlign: "left",
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(100, 200, 100, 0.4)",
                      background: "rgba(20, 60, 30, 0.6)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(30, 80, 40, 0.8)";
                      e.currentTarget.style.borderColor = "rgba(100, 200, 100, 0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(20, 60, 30, 0.6)";
                      e.currentTarget.style.borderColor = "rgba(100, 200, 100, 0.4)";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 2,
                        gap: 8,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 999,
                            overflow: "hidden",
                            border: "2px solid rgba(100, 200, 100, 0.5)",
                            background: "rgba(50, 80, 60, 0.9)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#86efac",
                            textTransform: "uppercase",
                          }}
                        >
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={`${companyName} logo`}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const fallback = e.currentTarget.nextSibling;
                                if (fallback && fallback.style) fallback.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <span style={{ display: logoUrl ? "none" : "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                            {companyName.charAt(0) || "?"}
                          </span>
                        </div>

                        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#f0fdf4" }}>
                          {companyName}
                        </span>
                      </div>

                      <div style={{ transform: "scale(0.6)", transformOrigin: "right center" }}>
                        <LevelBadge level={c.level || c.certificationLevel || "NONE"} size="small" showLabel={false} />
                      </div>
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.85, color: "#d1fae5" }}>
                      🌍 {c.country || "—"}
                    </div>
                    {c.walletAddress || c.wallet || c.address ? (
                      <div style={{ fontSize: 10, opacity: 0.75, wordBreak: "break-all", color: "#bbf7d0" }}>
                        🪪 {c.walletAddress || c.wallet || c.address}
                      </div>
                    ) : null}
                  </button>
                );
              })}
              {!loadingCompanies && companies.length === 0 && (
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  No companies yet. Add one from the admin panel.
                </div>
              )}
              {!loadingCompanies && companies.length > 0 && filteredCompanies.length === 0 && (
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  No companies match your search/filter.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* COLONNA DESTRA: GLOBO */}
        <section
          className="map-section"
          style={{
            position: "relative",
            borderRadius: 20,
            overflow: "hidden",
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.15)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            minHeight: 420,
          }}
        >
          {mapInitError ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#ff6b6b",
                padding: 20,
                textAlign: "center",
              }}
            >
              <div>
                <p style={{ marginBottom: 10 }}>⚠️ Map Error</p>
                <p style={{ fontSize: 12, opacity: 0.7 }}>{mapInitError}</p>
              </div>
            </div>
          ) : (
            <div
              ref={mapContainerRef}
              style={{ 
                width: "100%", 
                height: "100%",
                background: "transparent"
              }}
            />
          )}

          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={statsCardStyle}>
              <div style={{ opacity: 0.8, fontSize: 11 }}>
                Certified Companies
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {filteredCompanies.length}
                {filteredCompanies.length !== companies.length ? ` / ${companies.length}` : ""}
              </div>
            </div>
            <div style={statsCardStyle}>
              <div style={{ opacity: 0.8, fontSize: 11 }}>
                Countries Covered
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {countriesCovered}
                {countriesCovered !== totalCountriesCovered ? ` / ${totalCountriesCovered}` : ""}
              </div>
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: "50%",
              transform: "translateX(-50%)",
              padding: "6px 12px",
              borderRadius: 999,
              background: "rgba(15,23,42,0.9)",
              border: "1px solid rgba(148,163,184,0.5)",
              fontSize: 11,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "999px",
                background:
                  "radial-gradient(circle at 30% 30%, #4ade80, #15803d)",
              }}
            />
            AppleTree ESG Certified Companies
          </div>
        </section>
      </main>

      {/* Modal for ESG Request Details */}
      {selectedRequest && (
        <div
          onClick={() => setSelectedRequest(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "linear-gradient(145deg, rgba(15,23,42,0.98), rgba(30,41,59,0.98))",
              border: "1px solid rgba(148,163,184,0.4)",
              borderRadius: 16,
              padding: 24,
              maxWidth: 700,
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px rgba(0,0,0,0.7)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>ESG Request Details</h2>
              <button
                onClick={() => setSelectedRequest(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#f87171",
                  fontSize: 24,
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", gap: 12, fontSize: 13 }}>
              <div>
                <strong>Company:</strong> {selectedRequest.companyName || "N/A"}
              </div>
              <div>
                <strong>VAT Number:</strong> {selectedRequest.vatNumber || "N/A"}
              </div>
              <div>
                <strong>Email:</strong> {selectedRequest.email || "N/A"}
              </div>
              <div>
                <strong>Status:</strong>{" "}
                <span
                  style={{
                    color:
                      selectedRequest.status === "pending"
                        ? "#fbbf24"
                        : selectedRequest.status === "approved"
                        ? "#4ade80"
                        : "#f87171",
                    fontWeight: 600,
                  }}
                >
                  {selectedRequest.status}
                </span>
              </div>
              <div>
                <strong>Submitted:</strong> {formatEnglishDate(selectedRequest.createdAt)}
              </div>

              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(148,163,184,0.3)" }}>
                <strong style={{ display: "block", marginBottom: 8 }}>Answers ({selectedRequest.answers?.length || 0}):</strong>
                {selectedRequest.answers && selectedRequest.answers.length > 0 ? (
                  <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 12 }}>
                    {selectedRequest.answers.map((ans, i) => (
                      <div key={i} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: "1px solid rgba(148,163,184,0.15)" }}>
                        <div style={{ opacity: 0.8 }}>{ans.questionKey}</div>
                        <div>{ans.answer || "(no answer)"}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>No answers submitted.</div>
                )}
              </div>

              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(148,163,184,0.3)" }}>
                <strong style={{ display: "block", marginBottom: 8 }}>Attachments ({selectedRequest.attachments?.length || 0}):</strong>
                {selectedRequest.attachments && selectedRequest.attachments.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
                    {selectedRequest.attachments.map((att, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{att.questionKey}</div>
                          <div style={{ opacity: 0.8, fontSize: 11 }}>{att.fileName}</div>
                        </div>
                        <a
                          href={att.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 6,
                            background: "#0ea5e9",
                            color: "#f9fafb",
                            textDecoration: "none",
                            fontSize: 11,
                          }}
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>No attachments.</div>
                )}
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                {selectedRequest.status === "pending" && (
                  <>
                    <button
                      onClick={() => handleApproveESG(selectedRequest.id)}
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        borderRadius: 999,
                        border: "none",
                        background: "#16a34a",
                        color: "#f9fafb",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectESG(selectedRequest.id)}
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        borderRadius: 999,
                        border: "none",
                        background: "#dc2626",
                        color: "#f9fafb",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDownloadZip(selectedRequest.id)}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    borderRadius: 999,
                    border: "none",
                    background: "#7c3aed",
                    color: "#f9fafb",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Download ZIP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <LegalFooter />
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.7)",
  background: "rgba(15,23,42,0.9)",
  color: "#f9fafb",
  fontSize: 12,
  outline: "none",
};

const statsCardStyle = {
  padding: "10px 12px",
  borderRadius: 12,
  background:
    "linear-gradient(145deg, rgba(24,163,74,0.2), rgba(5,8,22,0.95))",
  border: "1.5px solid rgba(124,255,178,0.5)",
  boxShadow: "0 12px 25px rgba(0,0,0,0.5)",
  backdropFilter: "blur(8px)",
  color: "#f8fafc",
  textShadow: "0 2px 8px rgba(0,0,0,0.9)",
};

// Note: LegalFooter should be added at the end of the main return() JSX, before closing </div>
// Example: Add <LegalFooter /> just before the final </div> of the component

export default ESGDashboard;
